/**
 * Client-side observability — answers "how long was X on the site,
 * what did they try, and where did they get stuck" without bolting
 * on a third-party tracker.
 *
 * Three event streams flow to Pages Functions and land in the
 * FOYR_SESSIONS KV namespace:
 *   • /api/session/beat   — every VITE_OBSERVABILITY_HEARTBEAT_MS
 *                            while the tab is visible (rolling
 *                            session summary row).
 *   • /api/session/track  — named funnel events (nav, click, chat,
 *                            rage_click, etc.).
 *   • /api/session/error  — window.onerror + unhandledrejection
 *                            + the rage-click frustration signal.
 *
 * CLAUDE.md §1 — every throttle, key, and URL fragment lives in
 * env.ts. CLAUDE.md §5 — every fetch/send is wrapped so a failed
 * post never breaks the user's flow, but we still log to console
 * so the failure isn't silent.
 *
 * Only writes anything when getSession() has an email. Anonymous
 * traffic on the public marketing pages is not tracked.
 */
import { env } from "./env";
import { getSession } from "./session";

interface BeatPayload {
  readonly sessionId: string;
  readonly email: string;
  readonly startedAt: string;
  readonly lastAt: string;
  readonly route: string;
  readonly beats: number;
  readonly routesVisited: ReadonlyArray<string>;
  readonly ua: string;
  readonly tz: string;
  readonly viewport: { readonly w: number; readonly h: number };
}

interface TrackPayload {
  readonly sessionId: string;
  readonly email: string;
  readonly ts: string;
  readonly name: string;
  readonly route: string;
  readonly payload?: Record<string, unknown>;
}

interface ErrorPayload {
  readonly sessionId: string;
  readonly email: string;
  readonly ts: string;
  readonly kind: "onerror" | "unhandled_rejection" | "rage_click";
  readonly message: string;
  readonly route: string;
  readonly source?: string;
  readonly stack?: string;
  readonly target?: string;
}

interface ObservabilityState {
  readonly sessionId: string;
  startedAt: string;
  lastBeatAt: string;
  beats: number;
  routesVisited: Set<string>;
  errorsSent: number;
  lastClick: { ts: number; target: string; count: number } | null;
  beatTimer: number | null;
}

let state: ObservabilityState | null = null;
let started = false;

function makeSessionId(): string {
  // Stable per-tab, regenerated per new tab so two tabs don't merge.
  const key = `${env.VITE_LS_SESSION_ID_PREFIX}observability`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  sessionStorage.setItem(key, id);
  return id;
}

function currentRoute(): string {
  return typeof window === "undefined" ? "" : window.location.pathname + window.location.search;
}

function safePost(path: string, body: unknown): void {
  // sendBeacon survives page unloads — preferred for heartbeats and
  // the final tick. Falls back to fetch keepalive when unavailable.
  try {
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    const ok = navigator.sendBeacon?.(path, blob);
    if (ok) return;
  } catch {
    // sendBeacon can throw on quota limits — fall through to fetch.
  }
  fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch((err) => {
    // CLAUDE.md §5 — never swallow silently.
    // eslint-disable-next-line no-console
    console.warn("[observability] post failed", path, err);
  });
}

function buildBeatPayload(s: ObservabilityState, email: string): BeatPayload {
  return {
    sessionId: s.sessionId,
    email,
    startedAt: s.startedAt,
    lastAt: s.lastBeatAt,
    route: currentRoute(),
    beats: s.beats,
    routesVisited: Array.from(s.routesVisited),
    ua: navigator.userAgent,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    viewport: { w: window.innerWidth, h: window.innerHeight },
  };
}

function sendBeat(): void {
  if (!state) return;
  const session = getSession();
  if (!session?.email) return; // anonymous → no tracking
  state.lastBeatAt = new Date().toISOString();
  state.beats += 1;
  state.routesVisited.add(currentRoute());
  safePost("/api/session/beat", buildBeatPayload(state, session.email));
}

export function track(name: string, payload?: Record<string, unknown>): void {
  if (!state) return;
  const session = getSession();
  if (!session?.email) return;
  const body: TrackPayload = {
    sessionId: state.sessionId,
    email: session.email,
    ts: new Date().toISOString(),
    name,
    route: currentRoute(),
    ...(payload ? { payload } : {}),
  };
  safePost("/api/session/track", body);
}

function reportError(kind: ErrorPayload["kind"], message: string, extra: Partial<ErrorPayload>): void {
  if (!state) return;
  const session = getSession();
  if (!session?.email) return;
  if (state.errorsSent >= 50) return; // bound runaway error storms — 50 per session
  state.errorsSent += 1;
  const body: ErrorPayload = {
    sessionId: state.sessionId,
    email: session.email,
    ts: new Date().toISOString(),
    kind,
    message: message.slice(0, 1000),
    route: currentRoute(),
    ...extra,
  };
  safePost("/api/session/error", body);
}

function describeTarget(el: EventTarget | null): string {
  if (!(el instanceof Element)) return "";
  const id = el.id ? `#${el.id}` : "";
  const cls = typeof el.className === "string" && el.className
    ? `.${el.className.toString().split(/\s+/).slice(0, 2).join(".")}`
    : "";
  const text = el.textContent?.trim().slice(0, 40) ?? "";
  return `${el.tagName.toLowerCase()}${id}${cls}${text ? `[${text}]` : ""}`;
}

function detectRageClick(ev: MouseEvent): void {
  if (!state) return;
  const now = Date.now();
  const targetDesc = describeTarget(ev.target);
  const last = state.lastClick;
  if (last && now - last.ts < 1000 && last.target === targetDesc) {
    last.ts = now;
    last.count += 1;
    if (last.count >= 3) {
      reportError("rage_click", `${last.count} clicks within 1s on same target`, { target: targetDesc });
      state.lastClick = null; // reset so we don't fire repeatedly
    }
  } else {
    state.lastClick = { ts: now, target: targetDesc, count: 1 };
  }
}

function patchHistoryForRouteEvents(): void {
  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);
  history.pushState = function patchedPush(...args: Parameters<History["pushState"]>): void {
    origPush(...args);
    track("nav", { route: currentRoute(), via: "pushState" });
  };
  history.replaceState = function patchedReplace(...args: Parameters<History["replaceState"]>): void {
    origReplace(...args);
    track("nav", { route: currentRoute(), via: "replaceState" });
  };
  window.addEventListener("popstate", () => track("nav", { route: currentRoute(), via: "popstate" }));
}

export function startObservability(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  const sessionId = makeSessionId();
  const now = new Date().toISOString();
  state = {
    sessionId,
    startedAt: now,
    lastBeatAt: now,
    beats: 0,
    routesVisited: new Set<string>([currentRoute()]),
    errorsSent: 0,
    lastClick: null,
    beatTimer: null,
  };

  // First beat immediately so we know they arrived.
  sendBeat();

  // Heartbeat loop — visibility-aware so a hidden tab doesn't pollute.
  const intervalMs = 30_000;
  state.beatTimer = window.setInterval(() => {
    if (document.visibilityState === "visible") sendBeat();
  }, intervalMs);

  // Visibility transitions are themselves interesting — bounce signal.
  document.addEventListener("visibilitychange", () => {
    track(document.visibilityState === "visible" ? "tab_visible" : "tab_hidden");
    if (document.visibilityState === "visible") sendBeat();
  });

  // Final beat before unload so duration math has the closing timestamp.
  window.addEventListener("pagehide", () => {
    sendBeat();
    track("session_end");
  });

  // Route awareness (React Router uses pushState under the hood).
  patchHistoryForRouteEvents();

  // Error capture — window errors and unhandled promise rejections.
  window.addEventListener("error", (ev) => {
    reportError("onerror", ev.message || "uncaught error", {
      source: ev.filename ?? "",
      stack: ev.error instanceof Error ? ev.error.stack ?? "" : "",
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    reportError(
      "unhandled_rejection",
      reason instanceof Error ? reason.message : String(reason),
      { stack: reason instanceof Error ? reason.stack ?? "" : "" },
    );
  });

  // Rage clicks — cheapest behavioural frustration proxy.
  document.addEventListener("click", detectRageClick, { capture: true });
}

export function stopObservability(): void {
  if (!state) return;
  if (state.beatTimer !== null) window.clearInterval(state.beatTimer);
  state = null;
  started = false;
}
